import { Injectable, NgZone, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ServiceConstant } from '../utils/service-constant.util';

export type ChatStreamEventType =
  'open' | 'message' | 'token' | 'done' | 'chat_title_updated' | 'error' | 'close';

export interface ChatStreamEvent {
  type: ChatStreamEventType;
  content?: string;
  role?: 'user' | 'assistant';
  messageId?: string;
  data?: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private readonly ngZone = inject(NgZone);

  private socket?: WebSocket;
  private eventSource?: EventSource;
  private connecting = false;

  get isConnected(): boolean {
    return (
      this.socket?.readyState === WebSocket.OPEN ||
      this.eventSource?.readyState === EventSource.OPEN
    );
  }

  get isActive(): boolean {
    return this.isConnected || this.connecting || this.socket?.readyState === WebSocket.CONNECTING;
  }

  /** Insight chat: `/v1/restaurants/{id}/insights/{insightId}/chat/stream` */
  connect(restaurantId: string, insightId: string): Observable<ChatStreamEvent> {
    const path = `${ServiceConstant.apiPrefix}/restaurants/${restaurantId}/insights/${insightId}/chat/stream`;
    return this.connectStream(path);
  }

  /**
   * Opens a WebSocket (SSE fallback) for any stream path under the API host.
   * Common chat uses `/v1/restaurants/{id}/chats/{chatId}/stream`.
   */
  connectStream(streamPath: string): Observable<ChatStreamEvent> {
    return new Observable((subscriber) => {
      this.disconnect();

      let disposed = false;
      let opened = false;
      let fallbackUsed = false;
      let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

      const emit = (event: ChatStreamEvent) => {
        if (!disposed) {
          this.ngZone.run(() => subscriber.next(event));
        }
      };

      const startEventSource = () => {
        if (disposed || fallbackUsed) {
          return;
        }
        fallbackUsed = true;

        const httpUrl = this.buildHttpStreamUrl(streamPath);
        const es = new EventSource(httpUrl);
        this.eventSource = es;
        this.connecting = true;

        es.onopen = () => {
          this.connecting = false;
          opened = true;
          emit({ type: 'open' });
        };

        es.onmessage = (event: MessageEvent) => {
          const parsed = this.parseSocketPayload(String(event.data ?? ''));
          if (parsed) {
            emit(parsed);
          }
        };

        es.onerror = () => {
          this.connecting = false;
          emit({ type: 'error' });
        };
      };

      const connectWebSocket = () => {
        if (disposed) {
          return;
        }

        let wsUrl: string;
        try {
          wsUrl = this.buildWebSocketUrl(streamPath);
        } catch (err) {
          console.error(err);
          emit({ type: 'error' });
          return;
        }

        if (!environment.production) {
          console.info('[ChatService] WebSocket connect', wsUrl.split('?')[0]);
        }

        const socket = new WebSocket(wsUrl);
        this.socket = socket;
        this.connecting = true;
        let wsOpened = false;

        socket.onopen = () => {
          this.connecting = false;
          wsOpened = true;
          opened = true;
          emit({ type: 'open' });
        };

        socket.onmessage = (event: MessageEvent) => {
          const parsed = this.parseSocketPayload(String(event.data ?? ''));
          if (parsed) {
            emit(parsed);
          }
        };

        socket.onerror = () => {
          if (!wsOpened && !fallbackUsed) {
            socket.close();
            this.socket = undefined;
            startEventSource();
            return;
          }
          emit({ type: 'error' });
        };

        socket.onclose = () => {
          this.socket = undefined;
          this.connecting = false;

          if (!wsOpened && !fallbackUsed) {
            startEventSource();
            return;
          }

          if (disposed) {
            return;
          }

          emit({ type: 'close' });

          reconnectTimer = setTimeout(() => {
            if (!disposed && !this.eventSource) {
              connectWebSocket();
            }
          }, 1000);
        };
      };

      connectWebSocket();

      return () => {
        disposed = true;
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
        }
        this.disconnect();
      };
    });
  }

  disconnect(): void {
    this.connecting = false;

    if (this.socket) {
      const state = this.socket.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        this.socket.close();
      }
      this.socket = undefined;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = undefined;
    }
  }

  send(payload: unknown): void {
    if (!this.isConnected) {
      console.warn('Chat stream is not connected');
      return;
    }
    const body = typeof payload === 'string' ? payload : JSON.stringify(payload);
    this.socket?.send(body);
  }

  private buildWebSocketUrl(streamPath: string): string {
    return this.buildStreamUrl(streamPath, this.resolveWebSocketBaseUrl(), true);
  }

  private buildHttpStreamUrl(streamPath: string): string {
    return this.buildStreamUrl(streamPath, this.resolveHttpStreamBaseUrl(), false);
  }

  private buildStreamUrl(streamPath: string, base: string, useWebSocketProtocol: boolean): string {
    const path = streamPath.startsWith('/') ? streamPath : `/${streamPath}`;
    if (!path.includes('/stream')) {
      throw new Error(`[ChatService] Invalid stream path: ${streamPath}`);
    }

    const baseWithSlash = base.endsWith('/') ? base : `${base}/`;
    const url = new URL(path, baseWithSlash);
    if (useWebSocketProtocol) {
      url.protocol = url.protocol === 'https:' ? 'wss:' : url.protocol === 'http:' ? 'ws:' : 'wss:';
    }

    const auth = new URLSearchParams(this.buildAuthQuery());
    auth.forEach((value, key) => url.searchParams.set(key, value));
    return url.toString();
  }

  private buildAuthQuery(): string {
    const token = localStorage.getItem('userToken') ?? '';
    const params = new URLSearchParams({
      token,
      x_api_key: environment.apiKey,
      x_client_type: 'web',
    });
    return params.toString();
  }

  /** Prefer API host (wss) — must be absolute so the socket does not hit localhost:4200. */
  private resolveWebSocketBaseUrl(): string {
    const configured = environment.webSocketUrl?.trim();
    if (configured && !configured.includes('ws.dev.forknfind.com')) {
      return configured.replace(/\/$/, '');
    }
    const fromApi = ServiceConstant.baseUrl
      .replace(/^https:/, 'wss:')
      .replace(/^http:/, 'ws:')
      .replace(/\/$/, '');
    if (fromApi.startsWith('ws://') || fromApi.startsWith('wss://')) {
      return fromApi;
    }
    return 'wss://api.dev.forknfind.com';
  }

  private resolveHttpStreamBaseUrl(): string {
    return ServiceConstant.baseUrl.replace(/\/$/, '');
  }

  private parseSocketPayload(raw: string): ChatStreamEvent | null {
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }

    if (trimmed.startsWith('data:')) {
      return this.parseSocketPayload(trimmed.slice(5).trim());
    }

    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      const nested = parsed['data'] as Record<string, unknown> | undefined;
      const content = this.extractContent(parsed, nested);
      const role = this.extractRole(parsed, nested);
      const messageId = this.extractMessageId(parsed, nested);
      const eventType = String(parsed['type'] ?? parsed['event'] ?? '').toLowerCase();

      if (
        eventType === 'chat_title_updated' ||
        eventType === 'title_updated' ||
        eventType === 'chat_title'
      ) {
        const title = this.extractTitle(parsed, nested);
        return title ? { type: 'chat_title_updated', content: title, data: parsed } : null;
      }

      if (
        eventType === 'done' ||
        eventType === 'complete' ||
        eventType === 'completed' ||
        parsed['done'] === true
      ) {
        return { type: 'done', content, role, messageId, data: parsed };
      }

      if (
        eventType === 'token' ||
        eventType === 'chunk' ||
        eventType === 'delta' ||
        parsed['token'] != null
      ) {
        const token =
          content ?? String(parsed['token'] ?? parsed['chunk'] ?? parsed['delta'] ?? '');
        return token ? { type: 'token', content: token, role, messageId, data: parsed } : null;
      }

      if (content) {
        return { type: 'message', content, role, messageId, data: parsed };
      }

      const messageObj = parsed['message'];
      if (messageObj && typeof messageObj === 'object') {
        const msg = messageObj as Record<string, unknown>;
        const nestedContent = this.extractContent(msg);
        if (nestedContent) {
          return {
            type: 'message',
            content: nestedContent,
            role: this.extractRole(msg),
            messageId: this.extractMessageId(msg),
            data: { ...parsed, tokens: msg['tokens'] ?? parsed['tokens'] },
          };
        }
      }

      return null;
    } catch {
      return { type: 'token', content: trimmed };
    }
  }

  private extractContent(
    parsed: Record<string, unknown>,
    nested?: Record<string, unknown>,
  ): string | undefined {
    const value =
      parsed['content'] ??
      parsed['text'] ??
      parsed['message'] ??
      nested?.['content'] ??
      nested?.['text'] ??
      nested?.['message'];
    return typeof value === 'string' ? value : undefined;
  }

  private extractRole(
    parsed: Record<string, unknown>,
    nested?: Record<string, unknown>,
  ): 'user' | 'assistant' | undefined {
    const value = parsed['role'] ?? nested?.['role'];
    if (value === 'user' || value === 'assistant') {
      return value;
    }
    return undefined;
  }

  private extractMessageId(
    parsed: Record<string, unknown>,
    nested?: Record<string, unknown>,
  ): string | undefined {
    const value =
      parsed['messageId'] ??
      parsed['message_id'] ??
      nested?.['messageId'] ??
      nested?.['message_id'];
    return typeof value === 'string' ? value : undefined;
  }

  private extractTitle(
    parsed: Record<string, unknown>,
    nested?: Record<string, unknown>,
  ): string | undefined {
    const value =
      parsed['title'] ?? parsed['chat_title'] ?? nested?.['title'] ?? nested?.['chat_title'];
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }
}
